import React, { type FC } from 'react';
import {
  formatDate,
  formatFloat,
  getPropertyAddress,
} from '../../utility/utility_functions';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import {
  abandonFocus,
  maintainFocus,
  setFocusedIndex,
} from '../../redux/state/avmMapSlice';

type ActiveAvmCompsTableProps = {
  comps: any;
};

const tdStyling = 'p-2 text-xs';
const compsHeaders = [
  'MLS #',
  'Listed',
  'Address',
  'List Price',
  '$/ Sq Ft',
  'Sq Ft',
  'Beds/ Baths',
  'Year Build',
  'Stories',
  'CDOM',
];

const ActiveAvmCompsTable: FC<ActiveAvmCompsTableProps> = ({ comps }) => {
  const isActive = true;
  const dispatch = useDispatch<AppDispatch>();
  const focusedIndex = useSelector(
    (state: RootState) => state.avmMap.focusedIndex
  );
  const keepFocus = useSelector(
    (state: RootState) => state.avmMap.maintainFocus
  );

  return (
    <table
      id="active-avm-comps-table"
      className="table-auto divide-y divide-gray-300 rounded-xl w-full"
    >
      <thead className="rounded-xl">
        <tr className="sticky bg-indigo-600 text-white text-center rounded-xl">
          {compsHeaders?.map((h) => (
            <th key={h} className="p-2 text-sm text-bold">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      {comps?.map((comp, index) => {
        const linkToProperty = (text: string) => {
          const propertyRoute = `/properties/${comp?.property?.id}`;
          return (
            <a
              href={propertyRoute}
              rel="noopener noreferrer"
              target="_blank"
              className="text-indigo-600 hover:text-indigo-900"
            >
              {text}
            </a>
          );
        };

        const getSaleType = (): string => {
          const howSold = comp?.property?.how_sold;
          if (!howSold) return 'N/A';
          return howSold === '_1SLLR' ? 'Owner Financed' : howSold;
        };

        const clearEmphasis = () => {
          if (!keepFocus) {
            dispatch(setFocusedIndex(null));
          }
        };

        const setEmphasisHover = () => {
          if (!keepFocus) {
            dispatch(setFocusedIndex({ index, isActive }));
          }
        };

        const setEmphasisClick = () => {
          if (!keepFocus) {
            dispatch(maintainFocus());
          } else if (
            index === focusedIndex?.index &&
            focusedIndex?.isActive === isActive
          ) {
            dispatch(abandonFocus());
          } else {
            dispatch(setFocusedIndex({ index, isActive }));
          }
        };

        const getListPricePerSqft = (comp) => {
          const listPrice = comp?.property?.list_price;
          const sqft = comp?.property?.sqft;
          if (listPrice == null || sqft == null || sqft == 0) {
            return null;
          }

          return `$${formatFloat(listPrice / sqft, 2)}`;
        };

        return (
          <tbody key={comp.id}>
            <tr
              className={`${
                index === focusedIndex?.index &&
                focusedIndex?.isActive === isActive
                  ? 'bg-indigo-200'
                  : ''
              } text-xs text-center whitespace-nowrap border`}
              onMouseEnter={setEmphasisHover}
              onMouseLeave={clearEmphasis}
              onClick={setEmphasisClick}
            >
              <td className={tdStyling}>
                {comp?.property?.last_mls_id ?? 'N/A'}
              </td>
              <td className={tdStyling}>
                {formatDate(comp?.property?.on_market_date) ?? 'N/A'}
              </td>
              <td className={tdStyling}>
                {linkToProperty(getPropertyAddress(comp?.property))}
              </td>
              <td className={tdStyling}>
                {comp?.property?.list_price
                  ? `${'$'}` + formatFloat(comp?.property?.list_price, 0)
                  : 'N/A'}
              </td>
              <td className={tdStyling}>
                {getListPricePerSqft(comp) ?? 'N/A'}
              </td>
              <td className={tdStyling}>
                {formatFloat(comp?.property?.sqft, 0) ?? 'N/A'}
              </td>
              <td className={tdStyling}>
                {comp?.property?.bedrooms ?? 'N/A'} /{' '}
                {comp?.property?.bathrooms ?? 'N/A'}
              </td>
              <td className={tdStyling}>
                {comp?.property?.year_built ?? 'N/A'}
              </td>
              <td className={tdStyling}>{comp?.property?.stories ?? 'N/A'}</td>
              <td className={tdStyling}>
                {comp?.property?.cumulative_days_on_market ?? 'N/A'}
              </td>
            </tr>
          </tbody>
        );
      })}
    </table>
  );
};

export default ActiveAvmCompsTable;
